import { useState } from 'react';

/**
 * The composer's emoji panel.
 *
 * Only emoji from Unicode 12 and earlier, so every one of them has a glyph in
 * Windows 10's Segoe UI Emoji as well as on macOS, Android and iOS - a newer
 * one renders as an empty box there. Each is rendered in the colour-emoji font
 * stack (`--font-emoji`), never the text font: Inter and Vazirmatn carry plain
 * glyphs for a few of these (❤ ☕ ⭐ ⚠), which would otherwise win and show
 * as thin monochrome symbols.
 */
// prettier-ignore
const CATEGORIES = [
  {
    id: 'smileys',
    label: 'Smileys',
    icon: '😀',
    emoji: [
      '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '🙂', '🙃', '😉', '😊',
      '😇', '🥰', '😍', '🤩', '😘', '😋', '😛', '😜', '🤪', '🤗', '🤭', '🤫',
      '🤔', '🤐', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '😌', '😔',
      '😪', '😴', '😷', '🤒', '🤧', '🥵', '🥶', '🥴', '😵', '🤯', '🤠', '🥳',
      '😎', '🤓', '🧐', '😕', '😟', '🙁', '😮', '😯', '😲', '😳', '🥺', '😦',
      '😨', '😰', '😥', '😢', '😭', '😱', '😖', '😣', '😞', '😓', '😩', '😫',
      '🥱', '😤', '😡', '😠', '🤬', '😈', '💀', '🤡', '👻', '👽', '🤖', '💩',
    ],
  },
  {
    id: 'gestures',
    label: 'Gestures',
    icon: '👍',
    emoji: [
      '👍', '👎', '👌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆',
      '👇', '☝️', '✋', '🤚', '🖐️', '🖖', '👋', '👏', '🙌', '👐', '🤲', '🤝',
      '🙏', '✍️', '💪', '🦾', '🧠', '👀', '👁️', '👂', '🙋', '🙆', '🙅', '🤷',
      '🤦', '🙇', '💁', '🧑‍💻', '👨‍💻', '👩‍💻', '🕺', '💃', '🏃', '🚶', '🧘', '🎉',
    ],
  },
  {
    id: 'symbols',
    label: 'Hearts & symbols',
    icon: '❤️',
    emoji: [
      '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕',
      '💯', '✅', '☑️', '✔️', '❌', '❎', '⚠️', '⛔', '🚫', '❓', '❗', '‼️',
      '⭐', '🌟', '✨', '⚡', '🔥', '💥', '💫', '💡', '🔔', '📌', '📍', '🏁',
      '🟢', '🟡', '🔴', '🔵', '⏰', '⌛', '🔒', '🔓', '🔑', '♻️', '➡️', '⬅️',
    ],
  },
  {
    id: 'objects',
    label: 'Work & things',
    icon: '💼',
    emoji: [
      '💼', '📁', '📂', '📄', '📝', '📎', '🖇️', '✂️', '📅', '📆', '📊', '📈',
      '📉', '📋', '📦', '📮', '📧', '📞', '💻', '🖥️', '⌨️', '🖱️', '🖨️', '💾',
      '📱', '🔋', '🔌', '🛠️', '🔧', '🔨', '⚙️', '🐛', '🧪', '🚀', '🚢', '✈️',
      '☕', '🍵', '🍕', '🍔', '🍰', '🎂', '🍎', '🍫', '🎁', '🏆', '🎯', '🎵',
    ],
  },
] as const;

export function EmojiPicker({ onPick }: { onPick: (emoji: string) => void }) {
  const [categoryId, setCategoryId] = useState<(typeof CATEGORIES)[number]['id']>('smileys');
  const category = CATEGORIES.find((item) => item.id === categoryId) ?? CATEGORIES[0];

  return (
    <div className="emoji-popover" role="dialog" aria-label="Emoji">
      <div className="emoji-tabs" role="tablist" aria-label="Emoji categories">
        {CATEGORIES.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={item.id === categoryId}
            title={item.label}
            aria-label={item.label}
            className={item.id === categoryId ? 'emoji-tab emoji-tab--active' : 'emoji-tab'}
            onClick={() => setCategoryId(item.id)}
          >
            <span className="emoji-glyph">{item.icon}</span>
          </button>
        ))}
      </div>
      <p className="emoji-heading">{category.label}</p>
      <div className="emoji-grid" role="tabpanel">
        {category.emoji.map((emoji) => (
          <button
            key={emoji}
            type="button"
            aria-label={`Insert ${emoji}`}
            onClick={() => onPick(emoji)}
          >
            <span className="emoji-glyph">{emoji}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
